import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { 
  getUserPreferences, 
  updateUserPreferences, 
  resetUserPreferences,
  extractErrorMessage 
} from "../../lib/api";
import { usePlatformSettings } from "./PlatformSettingsContext";

/**
 * User Settings Context
 * Layers per-user preferences on top of platform defaults.
 * User settings override platform settings when set.
 * Null/unset values fall back to platform defaults.
 */

const UserSettingsContext = createContext(null);

// Font scale CSS variable mapping
const FONT_SCALE_VALUES = {
  "100%": 1,
  "110%": 1.1,
  "120%": 1.2,
};

export function UserSettingsProvider({ children }) {
  // Get platform defaults
  const { settings: platformSettings, loading: platformLoading } = usePlatformSettings();
  
  // User preferences state (sparse - only overrides)
  const [userPreferences, setUserPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch user preferences on mount
  useEffect(() => {
    const loadUserPreferences = async () => {
      try {
        const response = await getUserPreferences();
        setUserPreferences(response.data);
      } catch (error) {
        console.error("Failed to load user preferences:", extractErrorMessage(error));
        // Don't show error toast - just use platform defaults
        setUserPreferences({});
      } finally {
        setLoading(false);
      }
    };
    
    loadUserPreferences();
  }, []);

  // Merge user preferences with platform defaults
  // User settings override platform settings when set (not null)
  const mergedSettings = useMemo(() => {
    if (!platformSettings) return null;
    
    const prefs = userPreferences || {};
    
    return {
      // Localisation - user overrides platform
      date_format: prefs.date_format ?? platformSettings.date_format,
      time_format: prefs.time_format ?? platformSettings.time_format,
      timezone: prefs.timezone ?? platformSettings.timezone,
      currency: prefs.currency ?? platformSettings.currency,
      number_format: prefs.number_format ?? platformSettings.number_format,
      
      // Theme - user overrides platform
      theme_mode: prefs.theme_mode ?? platformSettings.theme_mode,
      
      // Accessibility - user overrides platform
      high_contrast_mode: prefs.high_contrast_mode ?? platformSettings.high_contrast_mode,
      reduced_motion: prefs.reduced_motion ?? false, // Platform doesn't have this yet
      font_scale: prefs.font_scale ?? "100%", // Default font scale
      
      // Brand colors from platform only (not user-configurable)
      primary_color: platformSettings.primary_color,
      secondary_color: platformSettings.secondary_color,
    };
  }, [platformSettings, userPreferences]);

  // Apply font scale to document
  useEffect(() => {
    const scale = FONT_SCALE_VALUES[mergedSettings?.font_scale] || 1;
    document.documentElement.style.setProperty("--user-font-scale", scale);
    document.documentElement.style.fontSize = `${scale * 100}%`;
    
    return () => {
      document.documentElement.style.removeProperty("--user-font-scale");
      document.documentElement.style.fontSize = "";
    };
  }, [mergedSettings?.font_scale]);

  // Apply reduced motion preference
  useEffect(() => {
    if (mergedSettings?.reduced_motion) {
      document.documentElement.classList.add("reduced-motion");
    } else {
      document.documentElement.classList.remove("reduced-motion");
    }
  }, [mergedSettings?.reduced_motion]);

  // Apply theme mode to document
  useEffect(() => {
    if (!mergedSettings?.theme_mode) return;
    
    const root = window.document.documentElement;
    let effectiveTheme = mergedSettings.theme_mode;
    
    // Handle "system" theme
    if (effectiveTheme === "system") {
      effectiveTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    
    // Remove existing theme classes and add new one
    root.classList.remove("light", "dark");
    root.classList.add(effectiveTheme);
    
    // Update localStorage to keep ThemeProvider in sync
    localStorage.setItem("eventsflo-theme", effectiveTheme);
  }, [mergedSettings?.theme_mode]);

  // Apply high contrast mode
  useEffect(() => {
    if (mergedSettings?.high_contrast_mode) {
      document.documentElement.classList.add("high-contrast");
    } else {
      document.documentElement.classList.remove("high-contrast");
    }
  }, [mergedSettings?.high_contrast_mode]);

  // Update a user preference
  // If value is null, clear the preference (revert to platform default)
  const updatePreference = useCallback(async (field, value) => {
    setSaving(true);
    try {
      let response;
      if (value === null) {
        // Clear the field to fall back to platform default
        response = await updateUserPreferences({ clear_fields: [field] });
        toast.success("Preference reset to platform default");
      } else {
        // Check if the value matches the platform default - if so, clear instead
        const platformDefault = platformSettings?.[field];
        if (value === platformDefault) {
          response = await updateUserPreferences({ clear_fields: [field] });
          toast.success("Preference reset to platform default");
        } else {
          response = await updateUserPreferences({ [field]: value });
          toast.success("Preference updated");
        }
      }
      setUserPreferences(response.data);
      return true;
    } catch (error) {
      toast.error(`Failed to update preference: ${extractErrorMessage(error)}`);
      return false;
    } finally {
      setSaving(false);
    }
  }, [platformSettings]);

  // Update multiple preferences at once
  const updatePreferences = useCallback(async (updates) => {
    setSaving(true);
    try {
      const response = await updateUserPreferences(updates);
      setUserPreferences(response.data);
      toast.success("Preferences updated");
      return true;
    } catch (error) {
      toast.error(`Failed to update preferences: ${extractErrorMessage(error)}`);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  // Clear a specific preference (revert to platform default)
  const clearPreference = useCallback(async (field) => {
    setSaving(true);
    try {
      const response = await updateUserPreferences({ clear_fields: [field] });
      setUserPreferences(response.data);
      toast.success("Preference reset to platform default");
      return true;
    } catch (error) {
      toast.error(`Failed to clear preference: ${extractErrorMessage(error)}`);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  // Clear multiple preferences
  const clearPreferences = useCallback(async (fields) => {
    setSaving(true);
    try {
      const response = await updateUserPreferences({ clear_fields: fields });
      setUserPreferences(response.data);
      toast.success("Preferences reset to platform defaults");
      return true;
    } catch (error) {
      toast.error(`Failed to clear preferences: ${extractErrorMessage(error)}`);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  // Reset all user preferences
  const resetAllPreferences = useCallback(async () => {
    setSaving(true);
    try {
      await resetUserPreferences();
      setUserPreferences({});
      toast.success("All preferences reset to platform defaults");
      return true;
    } catch (error) {
      toast.error(`Failed to reset preferences: ${extractErrorMessage(error)}`);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  // Check if a specific preference has a user override
  const hasOverride = useCallback((field) => {
    return userPreferences?.[field] !== null && userPreferences?.[field] !== undefined;
  }, [userPreferences]);

  // Get the user override value (or null if not set)
  const getOverride = useCallback((field) => {
    return userPreferences?.[field] ?? null;
  }, [userPreferences]);

  // Get the platform default value
  const getPlatformDefault = useCallback((field) => {
    return platformSettings?.[field] ?? null;
  }, [platformSettings]);

  const value = {
    // Merged settings (use this for display)
    settings: mergedSettings,
    
    // Raw user preferences (overrides only)
    userPreferences,
    
    // Platform defaults
    platformSettings,
    
    // Loading states
    loading: loading || platformLoading,
    saving,
    
    // Actions
    updatePreference,
    updatePreferences,
    clearPreference,
    clearPreferences,
    resetAllPreferences,
    
    // Helpers
    hasOverride,
    getOverride,
    getPlatformDefault,
  };

  return (
    <UserSettingsContext.Provider value={value}>
      {children}
    </UserSettingsContext.Provider>
  );
}

export function useUserSettings() {
  const context = useContext(UserSettingsContext);
  if (!context) {
    throw new Error("useUserSettings must be used within a UserSettingsProvider");
  }
  return context;
}

export default UserSettingsContext;
